import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, RefreshCw, X } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import productAnalyticsService from '../../../api/crm/analytics/productAnalyticsService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import useDebounce from '../../../hooks/useDebounce';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const formatCountAxis = (val) => {
  const n = Number(val) || 0;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const formatSalesAxis = (val) => {
  const n = Number(val) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${n}`;
};

const formatPctChange = (pct) => {
  const n = Number(pct) || 0;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n}%`;
};

const truncateLabel = (text, max = 20) => {
  const value = String(text || '').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
};

const MonthTopProductsTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-gray-900 mb-1">{row.productName}</p>
      <p className="text-gray-600">Revenue: ₹{formatIndianAmount(row.revenue || 0)}</p>
      <p className="text-gray-600">Units: {new Intl.NumberFormat('en-IN').format(row.unitsSold || 0)}</p>
    </div>
  );
};

const MonthTopProductsPanel = ({ monthData }) => {
  const chartData = useMemo(
    () => [...(monthData?.topProducts || [])]
      .sort((a, b) => (a.revenue || 0) - (b.revenue || 0))
      .map((product) => ({
        ...product,
        label: truncateLabel(product.productName, 22)
      })),
    [monthData]
  );

  const chartHeight = Math.max(140, chartData.length * 36 + 24);

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
      <div className="mb-2">
        <p className="text-sm font-semibold text-gray-900">{monthData.month}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Month total: ₹{formatIndianAmount(monthData.totalRevenue || 0)}
          {' · '}
          {new Intl.NumberFormat('en-IN').format(monthData.totalUnits || 0)} units
        </p>
      </div>
      {chartData.length ? (
        <div style={{ height: chartHeight }} className="w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis
                type="number"
                stroke="#94a3b8"
                fontSize={10}
                tickLine={{ stroke: '#cbd5e1' }}
                axisLine={{ stroke: '#94a3b8' }}
                tickFormatter={formatSalesAxis}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={108}
                stroke="#64748b"
                fontSize={10}
                tickLine={{ stroke: '#cbd5e1' }}
                axisLine={{ stroke: '#94a3b8' }}
              />
              <Tooltip content={<MonthTopProductsTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-[100px] text-gray-400 text-xs">No product sales</div>
      )}
    </div>
  );
};

const DEFAULT_FILTERS = {
  range: 'last_30_days',
  startDate: '',
  endDate: '',
  state: '',
  page: 1,
  limit: 10
};

const ProductAnalytics = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modular payload state
  const [payload, setPayload] = useState({
    summaryKpis: null,
    trendSeries: [],
    topProducts: [],
    topCustomers: [],
    stateBreakdown: [],
    productTable: null,
    topCustomersLeaderboard: null,
    monthComparison: null,
    sixMonthTrend: null,
    sixMonthTopProducts: null
  });

  const debouncedFilters = useDebounce(filters, 500);
  const abortControllerRef = useRef(null);

  const fetchSection = async (apiFunc, key, activeFilters, signal) => {
    try {
      const res = await apiFunc(activeFilters, signal);
      if (res.success) {
        setPayload(prev => ({ ...prev, [key]: res.data }));
      } else if (res.error && res.error !== 'canceled') {
      }
    } catch (err) {
    }
  };

  const fetchAnalytics = useCallback(async (activeFilters) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError('');

    // Start all requests in parallel
    const requests = [
      fetchSection(productAnalyticsService.getSummary, 'summaryKpis', activeFilters, controller.signal),
      fetchSection(productAnalyticsService.getTrend, 'trendSeries', activeFilters, controller.signal),
      fetchSection(productAnalyticsService.getTopItems, 'items', activeFilters, controller.signal),
      fetchSection(productAnalyticsService.getStateBreakdown, 'stateBreakdown', activeFilters, controller.signal),
      fetchSection(productAnalyticsService.getTopCustomersLeaderboard, 'topCustomersLeaderboard', activeFilters, controller.signal),
      fetchSection(productAnalyticsService.getTable, 'productTable', activeFilters, controller.signal),
      fetchSection(
        productAnalyticsService.getMonthComparison,
        'monthComparison',
        { state: activeFilters.state },
        controller.signal
      ),
      fetchSection(
        productAnalyticsService.getSixMonthTrend,
        'sixMonthTrend',
        { state: activeFilters.state },
        controller.signal
      ),
      fetchSection(
        productAnalyticsService.getSixMonthTopProducts,
        'sixMonthTopProducts',
        { state: activeFilters.state, topN: 5 },
        controller.signal
      )
    ];

    // Wait for all to finish for the overall loading state, 
    // but the UI will update as each one completes because of setPayload in fetchSection
    Promise.all(requests).finally(() => {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    });
  }, []);

  // Specialized effect for handling top items split response
  useEffect(() => {
    if (payload.items) {
      setPayload(prev => ({
        ...prev,
        topProducts: payload.items.topProducts || [],
        topCustomers: payload.items.topCustomers || [],
        items: undefined // clean up
      }));
    }
  }, [payload.items]);

  useEffect(() => {
    fetchAnalytics(debouncedFilters);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [debouncedFilters, fetchAnalytics]);

  const tableRows = payload.productTable?.rows || [];

  const kpiCards = useMemo(() => {
    const summary = payload.summaryKpis || {};
    return [
      { label: 'Net Sales', value: `₹${formatIndianAmount(summary.netSales || 0)}` },
      { label: 'Units Sold', value: new Intl.NumberFormat('en-IN').format(summary.unitsSold || 0) },
      { label: 'Return %', value: `${summary.returnRatePct || 0}%` },
      { label: 'Eff. Realization', value: `₹${formatIndianAmount(summary.effectiveRealization || 0)}` },
      { label: 'Active Products', value: new Intl.NumberFormat('en-IN').format(summary.activeProducts || 0) }
    ];
  }, [payload.summaryKpis]);

  const monthChartData = payload.monthComparison?.chartData || [];
  const monthChanges = payload.monthComparison?.changes || {};
  const sixMonthSeries = payload.sixMonthTrend?.series || [];
  const sixMonthTopProductMonths = payload.sixMonthTopProducts?.months || [];

  const exportCurrentTable = () => {
    if (!tableRows.length) return;
    const headers = ['Product', 'Product Code', 'Customer', 'State', 'Units Sold', 'Net Sales', 'Returns', 'Effective Realization'];
    const rows = tableRows.map((row) => ([
      row.productName, row.productCode, row.customerName, row.state,
      row.unitsSold, row.netSales, row.returnAmount, row.effectiveRealization
    ]));
    const csv = [headers, ...rows].map((line) => line.map((field) => `"${field}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `product-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onFilter = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value, page: 1 };
      if (key === 'range' && value !== 'custom') {
        newFilters.startDate = '';
        newFilters.endDate = '';
      }
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const topByInvoice = payload.topCustomersLeaderboard?.byInvoiceCount || [];
  const topByAmount = payload.topCustomersLeaderboard?.byAmount || [];

  const getCustomerReferenceId = (row) => {
    const referenceId = String(row?.customerReferenceId || row?.customerId || '').trim();
    return referenceId && referenceId !== 'N/A' ? referenceId : '';
  };

  const handleOpenCustomerDetail = (row) => {
    const referenceId = getCustomerReferenceId(row);
    if (referenceId) {
      navigate(`/customer_360/${encodeURIComponent(referenceId)}`);
      return;
    }
    const mongoId = String(row?.customerMongoId || '').trim();
    if (mongoId) {
      navigate(`/customer_360/${encodeURIComponent(mongoId)}`);
    }
  };

  const renderTopCustomerTable = (rows, metricKey, metricLabel) => (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['#', 'Customer ID', 'Customer Name', 'Phone', metricLabel].map((header) => (
              <th
                key={header}
                className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide ${
                  header === metricLabel ? 'text-right' : ''
                }`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const referenceId = getCustomerReferenceId(row);
            const rowKey = referenceId || row.customerMongoId || row.margCustomerId || index;
            return (
            <tr key={`${rowKey}-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-2 text-gray-500">{index + 1}</td>
              <td className="px-3 py-2 font-medium text-gray-900">
                {referenceId ? (
                  <button
                    type="button"
                    onClick={() => handleOpenCustomerDetail(row)}
                    className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                    title="View customer details"
                  >
                    {referenceId}
                  </button>
                ) : (
                  row.customerId || 'N/A'
                )}
              </td>
              <td className="px-3 py-2 text-gray-800">{row.customerName}</td>
              <td className="px-3 py-2 text-gray-600">{row.phone}</td>
              <td className="px-3 py-2 text-right font-semibold text-gray-900">
                {metricKey === 'invoiceCount'
                  ? new Intl.NumberFormat('en-IN').format(row.invoiceCount || 0)
                  : `₹${formatIndianAmount(row.totalAmount || 0)}`}
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>
      {!rows.length && !loading && (
        <div className="p-8 text-center text-gray-400 text-sm">No customers found for selected filters.</div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Analytics</h1>
          <p className="text-sm text-gray-600">Finance-safe net sales analytics.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border rounded-md px-3 py-2 text-sm bg-white min-w-[160px]"
            value={filters.range}
            onChange={(e) => onFilter('range', e.target.value)}
          >
            {['today', 'last_7_days', 'last_30_days', 'last_60_days', 'last_90_days', 'all_time', 'custom'].map((r) => (
              <option key={r} value={r}>{r.replace(/_/g, ' ').toUpperCase()}</option>
            ))}
          </select>
          {filters.range === 'custom' && (
            <DateRangeFilter
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={({ startDate, endDate }) => {
                setFilters((prev) => ({
                  ...prev,
                  startDate,
                  endDate,
                  page: 1
                }));
              }}
            />
          )}
          {filters.range === 'custom' && (filters.startDate || filters.endDate) && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50 flex items-center gap-2 text-red-600 border-red-100"
              type="button"
            >
              <X className="h-4 w-4" /> Clear
            </button>
          )}
          <button onClick={() => fetchAnalytics(filters)} className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50 flex items-center gap-2" type="button">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={exportCurrentTable} className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50 flex items-center gap-2" type="button">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-md p-3 text-sm">{error}</div>}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {kpiCards.map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-lg shadow-sm p-4 animate-in fade-in">
            <p className="text-xs text-gray-500 uppercase">{kpi.label}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {!payload.summaryKpis && loading ? '...' : kpi.value}
            </p>
          </div>
        ))}
      </div>

      {/* Month comparison */}
      <div className="space-y-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Last Month vs Current Month</h3>
          <p className="text-xs text-gray-500 mt-1">
            {payload.monthComparison?.periodNote
              || 'Sales invoices only — same day range in each month (e.g. 1–18 vs 1–18), not affected by date filter above'}
          </p>
        </div>
        {!payload.monthComparison && loading ? (
          <div className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-center h-[280px] text-gray-400 text-sm">
            Loading comparison...
          </div>
        ) : monthChartData.length ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Invoice Count</h4>
                {payload.monthComparison && (
                  <span className="px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs">
                    {formatPctChange(monthChanges.invoiceCountPct)} vs last month
                  </span>
                )}
              </div>
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthChartData} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#94a3b8' }} />
                    <YAxis stroke="#3b82f6" fontSize={12} tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#94a3b8' }} tickFormatter={formatCountAxis} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)' }}
                      formatter={(value) => [new Intl.NumberFormat('en-IN').format(value), 'Invoices']}
                    />
                    <Bar dataKey="invoiceCount" name="Invoice Count" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={56} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Sales Amount</h4>
                {payload.monthComparison && (
                  <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs">
                    {formatPctChange(monthChanges.salesAmountPct)} vs last month
                  </span>
                )}
              </div>
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthChartData} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#94a3b8' }} />
                    <YAxis stroke="#10b981" fontSize={12} tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#94a3b8' }} tickFormatter={formatSalesAxis} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)' }}
                      formatter={(value) => [`₹${formatIndianAmount(value)}`, 'Sales']}
                    />
                    <Bar dataKey="salesAmount" name="Sales Amount" fill="#10b981" radius={[4, 4, 0, 0]} barSize={56} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-center h-[200px] text-gray-400 text-sm">
            No sales data for comparison period.
          </div>
        )}
      </div>

      {/* 6-month trend */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1">Last 6 Months Trend</h3>
        <p className="text-xs text-gray-500 mb-4">
          Invoice count and sales amount by month (sales invoices only; current month is month-to-date)
        </p>
        {!payload.sixMonthTrend && loading ? (
          <div className="flex items-center justify-center h-[320px] text-gray-400 text-sm">Loading trend...</div>
        ) : sixMonthSeries.length ? (
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={sixMonthSeries} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#94a3b8' }} />
                <YAxis
                  yAxisId="invoices"
                  stroke="#3b82f6"
                  fontSize={12}
                  tickLine={{ stroke: '#cbd5e1' }}
                  axisLine={{ stroke: '#94a3b8' }}
                  tickFormatter={formatCountAxis}
                />
                <YAxis
                  yAxisId="sales"
                  orientation="right"
                  stroke="#10b981"
                  fontSize={12}
                  tickLine={{ stroke: '#cbd5e1' }}
                  axisLine={{ stroke: '#94a3b8' }}
                  tickFormatter={formatSalesAxis}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)' }}
                  formatter={(value, name) => {
                    if (name === 'Sales Amount') {
                      return [`₹${formatIndianAmount(value)}`, name];
                    }
                    return [new Intl.NumberFormat('en-IN').format(value), name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
                <Line
                  yAxisId="invoices"
                  type="monotone"
                  dataKey="invoiceCount"
                  name="Invoice Count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#3b82f6' }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="sales"
                  type="monotone"
                  dataKey="salesAmount"
                  name="Sales Amount"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#10b981' }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">No trend data available.</div>
        )}
      </div>

      {/* 6-month top products */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1">Last 6 Months — Top Products</h3>
        <p className="text-xs text-gray-500 mb-4">
          Top 5 products by revenue each month (sales line items); month header shows total units and revenue for all products
        </p>
        {!payload.sixMonthTopProducts && loading ? (
          <div className="flex items-center justify-center h-[240px] text-gray-400 text-sm">Loading top products...</div>
        ) : sixMonthTopProductMonths.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sixMonthTopProductMonths.map((monthData) => (
              <MonthTopProductsPanel key={monthData.period} monthData={monthData} />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">No product data for the last 6 months.</div>
        )}
      </div>

      {/* Charts / Highlights */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 min-h-[300px]">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Top Products</h3>
          {!payload.topProducts.length && loading ? (
             <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading products...</div>
          ) : (
            <div className="space-y-3">
              {payload.topProducts.slice(0, 8).map((item) => {
                const maxVal = payload.topProducts[0].netSales || 1;
                const barWidth = Math.max(5, ((item.netSales || 0) / maxVal) * 100);
                return (
                  <div key={`${item.productPid}`} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate pr-2">{item.productName}</span>
                      <span className="font-medium text-gray-900">₹{formatIndianAmount(item.netSales || 0)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded">
                      <div className="h-2 bg-blue-500 rounded transition-all duration-500" style={{ width: `${barWidth}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 min-h-[300px]">
          <h3 className="text-base font-semibold text-gray-900 mb-3">State Breakdown</h3>
          {!payload.stateBreakdown.length && loading ? (
             <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading states...</div>
          ) : (
            <div className="space-y-2">
              {payload.stateBreakdown.slice(0, 10).map((row) => (
                <div key={row.state} className="flex items-center justify-between border-b border-gray-100 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-800">{row.state}</p>
                    <p className="text-xs text-gray-500">{row.activeCustomers} customers | {row.unitsSold} units</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">₹{formatIndianAmount(row.netSales || 0)}</p>
                    <p className="text-xs text-gray-500">{row.contributionPct}% share</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top Customers */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Top 10 Customers by Invoice Count</h3>
          {!payload.topCustomersLeaderboard && loading ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading customers...</div>
          ) : (
            renderTopCustomerTable(topByInvoice, 'invoiceCount', 'Invoice Count')
          )}
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Top 10 Customers by Amount</h3>
          {!payload.topCustomersLeaderboard && loading ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading customers...</div>
          ) : (
            renderTopCustomerTable(topByAmount, 'totalAmount', 'Total Amount')
          )}
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg shadow-sm p-4 overflow-hidden">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Product-Customer-State Drilldown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Product', 'Customer', 'State', 'Units', 'Net', 'Returns', 'Realization'].map(h => 
                   <th key={h} className={`px-3 py-2 text-${['Units', 'Net', 'Returns', 'Realization'].includes(h) ? 'right' : 'left'}`}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody className={!payload.productTable && loading ? 'opacity-50' : ''}>
              {tableRows.map((row, idx) => (
                <tr key={`${row.productPid}-${row.customerCid}-${row.state}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2"><p className="font-medium text-gray-900">{row.productName}</p><p className="text-xs text-gray-500">{row.productCode}</p></td>
                  <td className="px-3 py-2">{row.customerName}</td>
                  <td className="px-3 py-2">{row.state}</td>
                  <td className="px-3 py-2 text-right">{row.unitsSold}</td>
                  <td className="px-3 py-2 text-right">₹{formatIndianAmount(row.netSales || 0)}</td>
                  <td className="px-3 py-2 text-right">₹{formatIndianAmount(row.returnAmount || 0)}</td>
                  <td className="px-3 py-2 text-right">₹{formatIndianAmount(row.effectiveRealization || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!tableRows.length && !loading && <div className="p-10 text-center text-gray-400">No data found for selected filters.</div>}
        </div>
      </div>

    </div>
  );
};

export default ProductAnalytics;
